// File: src/components/LearnAyurvedaPage.tsx

import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async'; // SEO के लिए मेटा टैग्स जोड़ने के लिए

const categories = [
  // { name: "The Doshas", path: "/learn-ayurveda/doshas" },
  { name: "Daily Routines", path: "/learn-ayurveda/routines" },
  { name: "Ayurvedic Herbs", path: "/learn-ayurveda/herbs" },
  { name: "Wellness & Lifestyle", path: "/learn-ayurveda/wellness" },
  { name: "Ayurvedic Diet", path: "/learn-ayurveda/diet" },
  { name: "Panchakarma", path: "/learn-ayurveda/panchakarma" },
];

const LearnAyurvedaPage: React.FC = () => {
  return (
    <main className="container mx-auto p-4 sm:p-6 lg:p-8">
      
      {/* --- SEO SECTION START --- */}
      <Helmet>
        <title>Ayushvatika | Authentic Ayurveda & Holistic Wellness in Noida</title>
        <meta 
          name="description" 
          content="Discover Ayushvatika in Noida - Your sanctuary for authentic Ayurvedic healing, natural products, and holistic wellness guidance rooted in ancient Indian traditions." 
        />
        <meta name="keywords" content="Ayurveda, Ayushvatika, Holistic Wellness, Ayurvedic Products, Noida, Natural Healing, Doshas" />
      </Helmet>
      {/* --- SEO SECTION END --- */}

      {/* Category Buttons Section - Navigation */}
      <nav className="flex flex-wrap justify-center gap-3 mb-8" aria-label="Ayurveda Categories">
        {categories.map((category) => (
          <Link
            key={category.name}
            to={category.path}
            className="text-sm px-4 py-2 border border-gray-300 rounded-full shadow-sm hover:bg-green-600 hover:text-white transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
          >
            {category.name}
          </Link>
        ))}
      </nav>

      {/* Main Content Area */}
      <article className="bg-white p-6 md:p-10 rounded-lg shadow-lg max-w-7xl mx-auto">
        
        <header className="mb-8 text-center">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            Ayushvatika: A Journey to Holistic Wellness with Ayurveda
          </h1>
        </header>

        <section className="mb-10">
          <p className="text-lg text-gray-700 leading-relaxed">
            Welcome to <strong>Ayushvatika</strong>, a sanctuary of ancient healing nestled in Noida, Uttar Pradesh, India. Our philosophy is rooted in the belief that true well-being is achieved when we live in harmony with nature's rhythms. We are dedicated to providing authentic Ayurvedic products and holistic guidance to help you find balance, health, and happiness in your life. Our mission is to bridge the gap between ancient traditions and modern lifestyles, making the profound benefits of Ayurveda accessible to everyone.
          </p>
        </section>

        <section className="my-12">
          {/* Responsive Image: Height adjusts based on screen size, object-cover prevents stretching */}
          <img
            src="https://krishnendu.org/wp-content/uploads/2018/12/7-Ayurvedic-herbs-to-add-to-daily-life.jpg" 
            alt="Collection of fresh Ayurvedic herbs and natural ingredients"
            loading="lazy"
            className="w-full h-64 md:h-96 lg:h-[500px] object-cover rounded-lg shadow-md mb-8"
          />
          <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 mb-4">
            Understanding Ayurveda: The Science of Life
          </h2>
          <p className="text-base text-gray-700 leading-relaxed mb-4">
            Ayurveda, derived from the Sanskrit words 'Ayur' (life) and 'Veda' (science or knowledge), is a 5,000-year-old system of natural healing that originated in India. It is not just a medical system but a way of life, focusing on prevention and the promotion of a balanced state of body, mind, and spirit. The core principle of Ayurveda is that every individual is a unique combination of the five elements: Earth, Water, Fire, Air, and Space. These elements manifest as three fundamental energies or biological humors known as the Doshas: <strong>Vata, Pitta, and Kapha</strong>.
          </p>
          <p className="text-base text-gray-700 leading-relaxed">
            At Ayushvatika, we help you understand your unique constitution (Prakriti) and the balance of your Doshas. This knowledge is the foundation for a personalized wellness plan, guiding your diet, daily routine, and choice of products to maintain optimal health.
          </p>
        </section>

        <section className="my-12">
          <img
            src="https://www.itoozhiayurveda.in/wp-content/uploads/2019/10/Ayurveda-for-Lifestyle-Diseases.jpg"
            alt="Healthy Ayurvedic lifestyle and balanced diet representation"
            loading="lazy"
            className="w-full h-auto max-h-[500px] object-cover rounded-lg shadow-md mb-8"
          />
          <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 mb-4">
            Our Commitment: Purity, Authenticity, and Education
          </h2>
          <p className="text-base text-gray-700 leading-relaxed mb-4">
            At the heart of Ayushvatika lies a deep commitment to purity and authenticity. Our products are made from the finest natural ingredients, sourced sustainably and prepared using traditional Ayurvedic methods as described in ancient texts. We believe in providing more than just products; we offer a path to a better life. We empower our customers with the knowledge to make informed decisions about their health and well-being.
          </p>
          <ul className="list-disc list-inside text-base text-gray-700 space-y-2 ml-2">
            <li><strong>Natural Ingredients:</strong> We use only pure, high-quality herbs and botanicals.</li>
            <li><strong>Traditional Methods:</strong> Our products are crafted following authentic Ayurvedic principles.</li>
            <li><strong>Holistic Wellness:</strong> We focus on the balance of the body, mind, and spirit.</li>
            <li><strong>Education and Guidance:</strong> We are committed to educating our customers about the principles of Ayurveda.</li>
          </ul>
        </section>

        <section className="my-12">
          <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 mb-6">
            Our Offerings: Products and Services
          </h2>
          <p className="text-base text-gray-700 leading-relaxed mb-8">
            Ayushvatika offers a wide range of Ayurvedic products and services designed to support your health journey:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-green-50 p-6 rounded-lg border border-green-100">
              <h3 className="text-xl md:text-2xl font-semibold text-green-800 mb-3">
                Ayurvedic Products
              </h3>
              <p className="text-base text-gray-700 leading-relaxed">
                Explore our collection of pure and potent Ayurvedic products for skincare, haircare, health supplements, and more. Each product is meticulously formulated to address specific wellness needs and promote natural healing from within.
              </p>
            </div>
            <div className="bg-green-50 p-6 rounded-lg border border-green-100">
              <h3 className="text-xl md:text-2xl font-semibold text-green-800 mb-3">
                Consultations & Guidance
              </h3>
              <p className="text-base text-gray-700 leading-relaxed">
                Our team of experienced Ayurvedic experts provides personalized consultations to help you understand your body's unique needs. We guide you on the right diet, lifestyle choices, and product usage based on your Dosha, helping you seamlessly integrate Ayurveda into your daily life.
              </p>
            </div>
          </div>
        </section>

        <footer className="mt-12 pt-8 border-t border-gray-200">
          <p className="text-lg text-gray-700 text-center leading-relaxed font-medium">
            Start your journey to a healthier, more balanced life with Ayushvatika. Embrace the power of nature and experience the transformative benefits of Ayurveda.
          </p>
        </footer>

      </article>
    </main>
  );
};

export default LearnAyurvedaPage;